/**
 * KPIDashboard - Tableau de bord KPI temps réel
 * Affiche les métriques clés : membres, souscriptions, CA, conversion
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, DollarSign, FileText, Download, Mail, BarChart3, Clock, Target, Award, Building, Calendar, Percent, UserPlus, FileCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../services/tokenService';
import InstitutionalHero from './ui/InstitutionalHero';
import RefreshButton from './ui/RefreshButton';
import { StatCard, MiniBarChart, CooperRanking } from './KPIWidgets';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Composant de statistique avec tendance

// Composant principal
export default function KPIDashboard({ userRole = 'super_admin' }) {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [trendPeriod, setTrendPeriod] = useState('7d');
  const [trends, setTrends] = useState([]);
  const [sendingReport, setSendingReport] = useState(false);
  
  const token = getToken();
  
  const fetchKPIs = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Récupérer les KPIs principaux
      const kpiRes = await axios.get(`${BACKEND_URL}/api/kpi/dashboard`, { headers });
      setKpis(kpiRes.data.kpis);
      
      // Récupérer le classement des COOPER'S (admin seulement)
      if (userRole === 'super_admin' || userRole === 'admin') {
        try {
          const rankingRes = await axios.get(`${BACKEND_URL}/api/kpi/coopers-ranking`, { headers });
          setRankings(rankingRes.data.ranking || []);
        } catch {
        }
      }
      
      // Récupérer les tendances
      const trendsRes = await axios.get(`${BACKEND_URL}/api/kpi/trends/${trendPeriod}`, { headers });
      setTrends(trendsRes.data.data || []);
      
    } catch {
      toast.error('Erreur lors du chargement des KPIs');
    } finally {
      setLoading(false);
    }
  }, [token, userRole, trendPeriod]);
  
  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);
  
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/kpi/download-report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rapport_KPI_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Rapport PDF téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };
  
  const handleSendReport = async (type = 'all') => {
    setSendingReport(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/kpi/send-report`,
        { type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rapport(s) envoyé(s) par email');
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingReport(false);
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-100 rounded-xl" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }
  
  const membres = kpis?.membres || {};
  const souscriptions = kpis?.souscriptions || {};
  const ca = kpis?.chiffre_affaires || {};
  const conversion = kpis?.conversion || {};
  
  return (
    <div className="space-y-6">
      {/* Hero institutionnel uniforme */}
      <InstitutionalHero
        icon={BarChart3}
        badge="Performance & Pilotage"
        title="Tableau de Bord KPI"
        subtitle={`Indicateurs temps réel : adhérents, souscriptions, chiffre d'affaires et conversion · Mis à jour le ${new Date(kpis?.generated_at).toLocaleString('fr-FR')}`}
        gradient="bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-900"
        iconColor="text-emerald-200"
        actions={
          <>
            <RefreshButton
              onRefresh={fetchKPIs}
              loading={loading}
              successMessage="KPI actualisés"
              variant="light"
              testId="kpi-refresh-btn"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            {(userRole === 'super_admin') && (
              <Button
                size="sm"
                onClick={() => handleSendReport('all')}
                disabled={sendingReport}
                className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold shadow-md"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingReport ? 'Envoi...' : 'Envoyer'}
              </Button>
            )}
          </>
        }
        kpis={[
          { label: 'Membres', value: (membres.total || 0).toLocaleString('fr-FR'), icon: Users, accent: 'bg-blue-400/25' },
          { label: 'Souscriptions', value: (souscriptions.validees || 0).toLocaleString('fr-FR'), icon: FileCheck, accent: 'bg-emerald-400/25' },
          { label: 'CA global', value: `${(ca.global?.total_ttc || 0).toLocaleString('fr-FR')}€`, icon: DollarSign, accent: 'bg-purple-400/25' },
          { label: 'Conversion', value: `${conversion.souscriptions?.taux_conversion || 0}%`, icon: Target, accent: 'bg-amber-400/25' },
        ]}
      />
      
      {/* KPIs principaux */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          staggerIndex={0}
          title="Membres Total"
          value={membres.total?.toLocaleString('fr-FR') || 0}
          subtitle={`+${membres.nouveaux_mois || 0} ce mois`}
          icon={Users}
          trend={membres.nouveaux_jour}
          trendValue={`+${membres.nouveaux_jour || 0} aujourd'hui`}
          color="blue"
        />
        <StatCard
          staggerIndex={1}
          title="Souscriptions Validées"
          value={souscriptions.validees?.toLocaleString('fr-FR') || 0}
          subtitle={`${souscriptions.taux_validation || 0}% taux validation`}
          icon={FileCheck}
          trend={souscriptions.nouvelles_mois}
          trendValue={`+${souscriptions.nouvelles_mois || 0} ce mois`}
          color="green"
        />
        <StatCard
          staggerIndex={2}
          title="CA Global"
          value={`${(ca.global?.total_ttc || 0).toLocaleString('fr-FR')}€`}
          subtitle={`+${(ca.global?.mois_ttc || 0).toLocaleString('fr-FR')}€ ce mois`}
          icon={DollarSign}
          color="purple"
          large
        />
        <StatCard
          staggerIndex={3}
          title="Taux Conversion"
          value={`${conversion.souscriptions?.taux_conversion || 0}%`}
          subtitle={`${conversion.souscriptions?.converties_30j || 0}/${(conversion.souscriptions?.simulations_30j || 0) + (conversion.souscriptions?.converties_30j || 0)} (30j)`}
          icon={Target}
          color="amber"
        />
      </div>
      
      {/* Détails en onglets */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Vue d&apos;ensemble</TabsTrigger>
          <TabsTrigger value="membres">Membres</TabsTrigger>
          <TabsTrigger value="souscriptions">Souscriptions</TabsTrigger>
          <TabsTrigger value="ca">Chiffre d&apos;Affaires</TabsTrigger>
          {(userRole === 'super_admin' || userRole === 'admin') && (
            <TabsTrigger value="coopers">Performance COOPER&apos;S</TabsTrigger>
          )}
        </TabsList>
        
        {/* Vue d'ensemble */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tendances */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Tendances</CardTitle>
                  <Select value={trendPeriod} onValueChange={setTrendPeriod}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">7 jours</SelectItem>
                      <SelectItem value="30d">30 jours</SelectItem>
                      <SelectItem value="90d">90 jours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-2">Nouveaux membres</p>
                  <MiniBarChart 
                    data={trends.slice(-7).map(d => ({ value: d.membres, label: d.label }))} 
                    color="blue" 
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-2">Souscriptions validées</p>
                  <MiniBarChart 
                    data={trends.slice(-7).map(d => ({ value: d.souscriptions, label: d.label }))} 
                    color="green" 
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-2">CA quotidien (€)</p>
                  <MiniBarChart 
                    data={trends.slice(-7).map(d => ({ value: d.ca, label: d.label }))} 
                    color="purple" 
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Répartition CA */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Répartition CA</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">RCDS</p>
                      <p className="text-sm text-gray-500">Contributions</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-blue-700">
                      {(ca.rcds?.total_ttc || 0).toLocaleString('fr-FR')}€
                    </p>
                    <p className="text-xs text-blue-600">
                      +{(ca.rcds?.mois_ttc || 0).toLocaleString('fr-FR')}€ ce mois
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Building className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">FTPE</p>
                      <p className="text-sm text-gray-500">Adhésions fédération</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-blue-700">
                      {(ca.ftpe?.total || 0).toLocaleString('fr-FR')}€
                    </p>
                    <p className="text-xs text-blue-600">
                      +{(ca.ftpe?.mois || 0).toLocaleString('fr-FR')}€ ce mois
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Conversion */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-600" />
                Taux de Conversion (30 derniers jours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium">Souscriptions</span>
                    <Badge className="bg-blue-700 text-white font-semibold">
                      {conversion.souscriptions?.taux_conversion || 0}%
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Simulations</span>
                      <span>{conversion.souscriptions?.simulations_30j || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Converties</span>
                      <span className="text-green-600 font-medium">{conversion.souscriptions?.converties_30j || 0}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${conversion.souscriptions?.taux_conversion || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium">FTPE</span>
                    <Badge className="bg-blue-700 text-white font-semibold">
                      {conversion.ftpe?.taux_conversion || 0}%
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Demandes</span>
                      <span>{conversion.ftpe?.demandes_30j || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Validées</span>
                      <span className="text-green-600 font-medium">{conversion.ftpe?.validees_30j || 0}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${conversion.ftpe?.taux_conversion || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Détails Membres */}
        <TabsContent value="membres">
          <Card>
            <CardHeader>
              <CardTitle>Détails Membres</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard title="Total" value={membres.total || 0} icon={Users} color="blue" />
                <StatCard title="Actifs" value={membres.actifs || 0} icon={UserPlus} color="green" />
                <StatCard title="En Attente" value={membres.en_attente || 0} icon={Clock} color="amber" />
                <StatCard title="FTPE Actifs" value={membres.ftpe_actifs || 0} icon={Building} color="purple" />
                <StatCard title="Ce Mois" value={`+${membres.nouveaux_mois || 0}`} icon={Calendar} color="blue" />
                <StatCard title="Taux Activation" value={`${membres.taux_activation || 0}%`} icon={Percent} color="green" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Détails Souscriptions */}
        <TabsContent value="souscriptions">
          <Card>
            <CardHeader>
              <CardTitle>Détails Souscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard title="Total" value={souscriptions.total || 0} icon={FileText} color="blue" />
                <StatCard title="Validées" value={souscriptions.validees || 0} icon={FileCheck} color="green" />
                <StatCard title="En Cours" value={souscriptions.en_cours || 0} icon={Clock} color="amber" />
                <StatCard title="Expirées" value={souscriptions.expirees || 0} icon={AlertTriangle} color="red" />
                <StatCard title="Avenants" value={souscriptions.avenants || 0} icon={FileText} color="purple" />
                <StatCard title="Taux Validation" value={`${souscriptions.taux_validation || 0}%`} icon={Percent} color="green" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Détails CA */}
        <TabsContent value="ca">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  RCDS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600">Total Montant de base</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {(ca.rcds?.total_ht || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600">Total Global</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {(ca.rcds?.total_ttc || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Ce Mois Montant de base</p>
                    <p className="text-2xl font-bold text-green-800">
                      {(ca.rcds?.mois_ht || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Ce Mois Global</p>
                    <p className="text-2xl font-bold text-green-800">
                      {(ca.rcds?.mois_ttc || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-amber-600" />
                  FTPE
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-600">Total</p>
                    <p className="text-2xl font-bold text-amber-800">
                      {(ca.ftpe?.total || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600">Ce Mois</p>
                    <p className="text-2xl font-bold text-green-800">
                      {(ca.ftpe?.mois || 0).toLocaleString('fr-FR')}€
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Performance COOPER'S (admin only) */}
        {(userRole === 'super_admin' || userRole === 'admin') && (
          <TabsContent value="coopers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-yellow-500" />
                  Classement des COOPER&apos;S
                </CardTitle>
                <CardDescription>Top 5 par chiffre d&apos;affaires généré</CardDescription>
              </CardHeader>
              <CardContent>
                <CooperRanking rankings={rankings} />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
